import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { RefreshCw, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (n) => '$ ' + Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s) => (s || '').slice(0, 10).split('-').reverse().join('/');
const KINDS = [
  { v: '', label: 'Todos' },
  { v: 'advance', label: 'Anticipos de cobro' },
  { v: 'nc_credit', label: 'Créditos de NC' },
  { v: 'adjustment_credit', label: 'Ajustes a favor' },
  { v: 'adjustment_debit', label: 'Ajustes que aumentaron deuda' },
];

export const UnappliedCreditsPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kind, setKind] = useState('');
  const [onlyDup, setOnlyDup] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${BACKEND_URL}/api/maintenance/unapplied-credits`, { withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo cargar'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const rows = useMemo(() => (data?.items || []).filter(i =>
    (!kind || i.kind === kind) && (!onlyDup || i.possible_duplicate)), [data, kind, onlyDup]);
  const total = rows.reduce((s, i) => s + (i.remaining || 0), 0);
  const resCount = new Set(rows.map(i => i.resident_id)).size;

  return (
    <Card data-testid="unapplied-credits-card">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-base">Pendientes de imputar (residentes)</CardTitle>
        <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="unapplied-reload-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />Actualizar
        </Button>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <p className="text-xs text-muted-foreground">
          Anticipos y ajustes de cta. cte. con saldo sin aplicar a un comprobante. Abrí la ficha y usá <b>Cuenta Cte. → Aplicar a comprobante</b>. Solo lectura: esta pantalla no modifica datos.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-9 rounded-md border px-2 text-sm" data-testid="unapplied-kind-filter">
            {KINDS.map(k => <option key={k.v} value={k.v}>{k.label}{k.v && data?.by_kind?.[k.v] ? ` · ${fmt(data.by_kind[k.v])}` : ''}</option>)}
          </select>
          <label className="text-xs flex items-center gap-1 cursor-pointer">
            <input type="checkbox" checked={onlyDup} onChange={(e) => setOnlyDup(e.target.checked)} data-testid="unapplied-dup-filter" />
            Solo posibles duplicados ({data?.duplicates || 0})
          </label>
          <span className="ml-auto text-xs" data-testid="unapplied-summary">{rows.length} registro(s) · {resCount} residente(s) · Total {fmt(total)}</span>
        </div>
        <div className="max-h-[480px] overflow-auto border rounded-md">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Residente</TableHead><TableHead>Tipo</TableHead><TableHead>Comprobante</TableHead>
              <TableHead>Fecha</TableHead><TableHead className="text-right">Monto</TableHead>
              <TableHead className="text-right">Sin imputar</TableHead><TableHead className="text-right">Saldo ficha</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {rows.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-6">{loading ? 'Cargando…' : 'Sin pendientes'}</TableCell></TableRow>
              ) : rows.map((i, idx) => (
                <TableRow key={`${i.kind}-${i.number}-${idx}`} data-testid={`unapplied-row-${idx}`}>
                  <TableCell className="text-xs font-medium">{i.resident_name}{i.is_archived && <Badge variant="outline" className="ml-1 text-[9px]">Archivada</Badge>}</TableCell>
                  <TableCell className="text-xs">
                    <span className={i.kind === 'adjustment_debit' ? 'text-amber-700' : 'text-emerald-700'}>{i.kind_label}</span>
                    {i.possible_duplicate && <Badge className="ml-1 bg-red-100 text-red-700 text-[9px]" data-testid={`unapplied-dup-${idx}`}>Posible duplicado</Badge>}
                  </TableCell>
                  <TableCell className="text-xs font-mono">{i.number}</TableCell>
                  <TableCell className="text-xs">{fmtDate(i.date)}</TableCell>
                  <TableCell className="text-xs text-right">{fmt(i.amount)}</TableCell>
                  <TableCell className="text-xs text-right font-semibold">{fmt(i.remaining)}</TableCell>
                  <TableCell className={`text-xs text-right ${i.cc_balance > 0 ? 'text-red-600' : 'text-emerald-700'}`}>{fmt(i.cc_balance)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.open(`/residents?open=${i.resident_id}&tab=cc`, '_blank')} data-testid={`unapplied-open-${idx}`}>
                      <ExternalLink className="w-3.5 h-3.5 mr-1" />Ficha
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};
